<template>
  <div class="admin_email_validation" data-testid="admin_email_validation">
    <v-toolbar grey>
      <v-toolbar-title>Email validation (admin)</v-toolbar-title>
    </v-toolbar>
    <v-container>
      <v-alert v-if="error_message" type="error" dismissible class="mb-4" @input="error_message = ''">
        {{ error_message }}
      </v-alert>

      <v-card>
        <v-card-text>
          <v-row dense align="center">
            <v-col cols="12" sm="8" md="9">
              <v-text-field
                v-model="email_input"
                label="Email"
                outlined
                dense
                clearable
                hide-details="auto"
                :readonly="has_loaded"
                data-testid="admin_email_validation_input"
                @keyup.enter="onEmailEnter"
              />
            </v-col>
            <v-col cols="12" sm="4" md="3" class="d-flex flex-wrap align-center">
              <v-btn v-if="!has_loaded" color="primary" block :loading="loading_lookup" :disabled="!email_trimmed" @click="lookup">
                Look up
              </v-btn>
              <v-btn v-else text color="primary" block @click="reset">
                <v-icon left small>edit</v-icon>
                Change email
              </v-btn>
            </v-col>
          </v-row>

          <template v-if="has_loaded">
            <v-row dense class="mt-3">
              <v-col cols="12">
                <v-text-field v-model="validation.address" label="address" outlined dense readonly hide-details="auto" />
              </v-col>
              <v-col cols="6" sm="6">
                <v-switch v-model="validation.is_disposable_address" label="Disposable" dense hide-details />
              </v-col>
              <v-col cols="6" sm="6">
                <v-switch v-model="validation.is_role_address" label="Role address" dense hide-details />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select v-model="validation.result" :items="result_items" label="result" outlined dense hide-details="auto" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select v-model="validation.risk" :items="risk_items" label="risk" outlined dense hide-details="auto" />
              </v-col>
              <v-col cols="12">
                <v-combobox
                  v-model="validation.reason"
                  label="reasons"
                  outlined
                  dense
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="validation.did_you_mean" label="did_you_mean (optional)" outlined dense clearable hide-details="auto" />
              </v-col>
            </v-row>

            <div class="mt-4">
              <v-btn color="secondary" class="mr-2" :loading="loading_revalidate" @click="revalidate">
                Revalidate
              </v-btn>
              <v-btn color="primary" :loading="loading_save" @click="saveRow">
                Save
              </v-btn>
            </div>
          </template>
        </v-card-text>
      </v-card>

      <v-snackbar v-model="snackbar" :timeout="3000" color="success" top>
        {{ snackbar_text }}
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import axios from "axios";
import {
  MAILGUN_VALIDATION_RESULT_OPTIONS,
  MAILGUN_VALIDATION_RISK_OPTIONS,
} from "../../classes/email";

const empty_validation = () => ({
  address: "",
  is_disposable_address: false,
  is_role_address: false,
  reason: [],
  result: "unknown",
  risk: "unknown",
  did_you_mean: "",
});

export default {
  name: "AdminEmailValidation",
  data() {
    return {
      has_loaded: false,
      email_input: "",
      loading_lookup: false,
      loading_revalidate: false,
      loading_save: false,
      error_message: "",
      snackbar: false,
      snackbar_text: "",
      validation: empty_validation(),
    };
  },
  computed: {
    email_trimmed() {
      return (this.email_input || "").trim();
    },
    result_items() {
      return MAILGUN_VALIDATION_RESULT_OPTIONS.map((o) => ({ text: o.text, value: o.value }));
    },
    risk_items() {
      return MAILGUN_VALIDATION_RISK_OPTIONS.map((o) => ({ text: o.text, value: o.value }));
    },
  },
  methods: {
    onEmailEnter() {
      if (!this.has_loaded) {
        this.lookup();
      }
    },
    handle_api_error(resp) {
      if (resp.data && resp.data.status === "error") {
        this.error_message = resp.data.error_message || "Request failed";
      } else {
        this.error_message = "Request failed";
      }
    },
    apply_validation_from_api(v) {
      this.validation = {
        address: v.address || this.email_trimmed,
        is_disposable_address: !!v.is_disposable_address,
        is_role_address: !!v.is_role_address,
        reason: Array.isArray(v.reason) ? [...v.reason] : [],
        result: v.result || "unknown",
        risk: v.risk || "unknown",
        did_you_mean: v.did_you_mean != null && v.did_you_mean !== undefined ? String(v.did_you_mean) : "",
      };
    },
    reset() {
      this.has_loaded = false;
      this.error_message = "";
      this.validation = empty_validation();
    },
    async lookup() {
      this.error_message = "";
      const email = this.email_trimmed;
      if (!email) {
        this.error_message = "Enter an email address.";
        return;
      }
      this.loading_lookup = true;
      try {
        const response = await axios.post("/api/email/check?use_cache=true", { email });
        if (response.data.status !== "success") {
          this.handle_api_error(response);
          return;
        }
        this.apply_validation_from_api(response.data.data);
        this.has_loaded = true;
      } catch (e) {
        if (e.response) {
          this.handle_api_error(e.response);
        } else {
          this.error_message = e.message || "Network error";
        }
      } finally {
        this.loading_lookup = false;
      }
    },
    async revalidate() {
      this.error_message = "";
      this.loading_revalidate = true;
      try {
        const response = await axios.post("/api/email/check?use_cache=false", { email: this.email_trimmed });
        if (response.data.status !== "success") {
          this.handle_api_error(response);
          return;
        }
        this.apply_validation_from_api(response.data.data);
        this.snackbar_text = "Revalidated.";
        this.snackbar = true;
      } catch (e) {
        if (e.response) {
          this.handle_api_error(e.response);
        } else {
          this.error_message = e.message || "Network error";
        }
      } finally {
        this.loading_revalidate = false;
      }
    },
    build_validation_payload() {
      const vs = {
        address: this.validation.address || this.email_trimmed,
        is_disposable_address: this.validation.is_disposable_address,
        is_role_address: this.validation.is_role_address,
        reason: this.validation.reason.filter((r) => String(r).trim() !== ""),
        result: this.validation.result,
        risk: this.validation.risk,
      };
      const dym = (this.validation.did_you_mean || "").trim();
      if (dym) {
        vs.did_you_mean = dym;
      }
      return vs;
    },
    async saveRow() {
      this.error_message = "";
      this.loading_save = true;
      try {
        const response = await axios.post("/api/email/validation", this.build_validation_payload());
        if (response.data.status !== "success") {
          this.handle_api_error(response);
          return;
        }
        this.snackbar_text = "Saved.";
        this.snackbar = true;
      } catch (e) {
        if (e.response) {
          this.handle_api_error(e.response);
        } else {
          this.error_message = e.message || "Network error";
        }
      } finally {
        this.loading_save = false;
      }
    },
  },
};
</script>
